import React, { useEffect, useRef, useState } from "react";
import { Card, Container, Spinner } from "reactstrap";
import { useNotification } from "providers/NotificationProvider";
import userServices from "services/userServices";
import PromoCodesTable from "./PromoCodesTable";
import PromoCodeModal from "./PromoCodeModal";
import DeleteModal from "../../../modals/DeleteModal";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import { useTranslation } from "context/TranslationProvider";

const AllPromoCodes = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const notifyRef = useRef(notify);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(undefined);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const res = await userServices.fetchPromoCodes();
        const data = res?.data?.data;
        if (isMounted) setItems(Array.isArray(data) ? data : []);
      } catch {
        notifyRef.current("error", t("promoCodes.toast.fetchFailed", "Failed to load promo codes"));
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  const handleSave = async (payload, id) => {
    try {
      if (id) {
        const res = await userServices.updatePromoCode(id, payload);
        const dataObj = res?.data || res;
        const updated = dataObj?.data || dataObj;
        
        if (dataObj?.success || updated?._id) {
          setItems((prev) => prev.map((item) => (item._id === id ? { ...item, ...updated } : item)));
          notify("success", t(dataObj?.messageKey || "promoCodes.toast.updateSuccess", dataObj?.message || t("promoCodes.toast.updateSuccess", "Promo code updated")));
        } else {
          notify("error", t(dataObj?.messageKey || dataObj?.error || "promoCodes.toast.saveFailed", dataObj?.message || t("promoCodes.toast.saveFailed", "Failed to save promo code")));
        }
      } else {
        const res = await userServices.createPromoCode(payload);
        const dataObj = res?.data || res;
        const created = dataObj?.data || dataObj;
        if (dataObj?.success || created?._id) {
          if (created) setItems((prev) => [created, ...prev]);
          notify("success", t(dataObj?.messageKey || "promoCodes.toast.createSuccess", dataObj?.message || t("promoCodes.toast.createSuccess", "Promo code created")));
        } else {
          notify("error", t(dataObj?.messageKey || dataObj?.error || "promoCodes.toast.saveFailed", dataObj?.message || t("promoCodes.toast.saveFailed", "Failed to save promo code")));
        }
      }
      setEditTarget(undefined);
    } catch (err) {
      const errPayload = err?.response?.data || {};
      notify("error", t(errPayload?.messageKey || errPayload?.error || "promoCodes.toast.saveFailed", errPayload?.message || errPayload?.error || t("promoCodes.toast.saveFailed", "Failed to save promo code")));
    }
  };

  const handleDelete = () => {
    if (!deleteTarget?._id) return;

    userServices
      .deletePromoCode(deleteTarget._id)
      .then((res) => {
        const dataObj = res?.data || res;
        if (dataObj?.success) {
          notify("success", t(dataObj?.messageKey || "promoCodes.toast.deleteSuccess", dataObj?.message || t("promoCodes.toast.deleteSuccess", "Promo code deleted")));
          setItems((prev) => prev.filter((item) => item._id !== deleteTarget._id));
        } else {
          notify("error", t(dataObj?.messageKey || dataObj?.error || "promoCodes.toast.deleteFailed", dataObj?.message || t("promoCodes.toast.deleteFailed", "Failed to delete promo code")));
        }
      })
      .catch((err) => {
        const errPayload = err?.response?.data || {};
        notify("error", t(errPayload?.messageKey || errPayload?.error || "promoCodes.toast.deleteFailed", errPayload?.message || t("promoCodes.toast.deleteFailed", "Failed to delete promo code")));
      })
      .finally(() => setDeleteTarget(null));
  };

  const renderLoading = () => (
    <div className="center-loader">
      <div className="center-loader-content">
        <Spinner />
        <p className="mt-3 text-muted">{t("common.loading", "Loading....")}</p>
      </div>
    </div>
  );

  return (
    <div className="card-header-full-height">
      <SimpleHeader
        name={t("sideNav.promoCodes", "Promo Codes")}
        parentName={t("sideNav.dashboard", "Dashboard")}
      />

      <Container className="mt--6" fluid>
        <Card className="shadow">
          {loading ? (
            renderLoading()
          ) : (
            <PromoCodesTable
              items={items}
              onEdit={(row) => setEditTarget(row ?? null)}
              deleteRow={(row) => setDeleteTarget(row)}
            />
          )}
        </Card>
      </Container>

      <PromoCodeModal
        isOpen={editTarget !== undefined}
        toggle={() => setEditTarget(undefined)}
        onSave={handleSave}
        editItem={editTarget}
      />

      <DeleteModal
        isOpen={Boolean(deleteTarget)}
        toggle={() => setDeleteTarget(null)}
        itemName="promoCode"
        onDelete={handleDelete}
      />
    </div>
  );
};

export default AllPromoCodes;
